import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useApiClient } from "../hooks/use-api-client.ts";
import type { SessionInfo } from "../api/client.ts";

const SessionContext = createContext<SessionInfo | null>(null);

/**
 * Fetches the device's company (GET /session) once at startup and makes it available
 * app-wide. If the call fails (e.g. dev with no Worker), the value stays null and the
 * company banner simply doesn't render — the app still works.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const api = useApiClient();
  const [session, setSession] = useState<SessionInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getSession()
      .then((s) => {
        if (!cancelled) setSession(s);
      })
      .catch(() => {
        /* no company banner if we can't resolve the session */
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

/** The resolved company for this device, or null while loading / on failure. */
export function useSession(): SessionInfo | null {
  return useContext(SessionContext);
}
